import { useState, useEffect } from "react";
import {
  Tv,
  Plus,
  X,
  Search,
  Wifi,
  WifiOff,
  ChevronDown,
  ChevronRight,
  History,
  Edit2,
  Play,
  AlertCircle,
  Film,
  Image,
  Calendar,
  Clock,
  Filter,
  Globe,
  Download,
} from "lucide-react";
import { useApiClient } from "../../providers/AuthProvider";
import { useTenant } from "../../providers/TenantProvider";
import {
  LABEL_CONFIG,
  type PermissionGroup,
  getChildGroups as getChildGroupsUtil,
  getRootGroups as getRootGroupsUtil,
} from "../../components/HierarchyPicker/types";
import { HierarchyPicker } from "../../components/HierarchyPicker/HierarchyPicker";
import PlaybackBars from "./PlaybackBars";

// ============================================================================
// Types
// ============================================================================

type Device = {
  id: string;
  tenant_id: string;
  site_id: string | null;
  label: string | null;
  hardware_id: string | null;
  status: "pending" | "registered" | "inactive";
  orientation: "landscape" | "portrait-left" | "portrait-right" | "landscape-inverted";
  tags: string[];
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  last_seen_at: string | null;
  isOnline: boolean;
};

type DeviceEvent = {
  id: string;
  tenantId: string;
  eventType: string;
  actorType: "device" | "user" | "system";
  actorId: string | null;
  resourceType: string | null;
  resourceId: string | null;
  data: Record<string, unknown>;
  createdAt: string;
};

type DevicePlaybackSummary = {
  deviceId: string;
  playbackSeconds24h: number;
  playbackSeconds7d: number;
  dailyPlaybackSeconds7d: number[];
  lastPlaybackAt: string | null;
};

type GroupWithDeviceCounts = PermissionGroup & {
  onlineCount: number;
  offlineCount: number;
};

// ============================================================================
// StatusBadge
// ============================================================================

const StatusBadge = ({ isOnline }: { isOnline: boolean }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full ${
      isOnline ? "bg-green-500/20 text-green-400" : "bg-zinc-700 text-zinc-400"
    }`}
  >
    {isOnline ? <Wifi size={12} /> : <WifiOff size={12} />}
    {isOnline ? "Online" : "Offline"}
  </span>
);

const formatOrientationLabel = (orientation: Device["orientation"]): string => {
  switch (orientation) {
    case "portrait-left":
      return "Portrait Left";
    case "portrait-right":
      return "Portrait Right";
    case "landscape-inverted":
      return "Landscape Inverted";
    default:
      return "Landscape";
  }
};

// ============================================================================
// DeviceRow
// ============================================================================

const formatPlaybackTime = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

type DeviceRowProps = {
  device: Device;
  groups: PermissionGroup[];
  playbackSeconds24h?: number;
  playbackSeconds7d?: number;
  dailyPlaybackSeconds7d?: number[];
  lastPlaybackAt?: string | null;
  onEdit: (device: Device) => void;
  onViewHistory: (device: Device) => void;
};

const DeviceRow = ({
  device,
  groups,
  playbackSeconds24h = 0,
  playbackSeconds7d = 0,
  dailyPlaybackSeconds7d = [0, 0, 0, 0, 0, 0, 0],
  lastPlaybackAt,
  onEdit,
  onViewHistory,
}: DeviceRowProps) => {
  const lastSeenDate = device.last_seen_at ? new Date(device.last_seen_at) : null;
  const fallbackDate = lastPlaybackAt ? new Date(lastPlaybackAt) : null;
  const lastSeenText = device.isOnline
    ? "Now"
    : lastSeenDate
      ? lastSeenDate.toLocaleString([], { dateStyle: "short", timeStyle: "short" })
      : fallbackDate
        ? fallbackDate.toLocaleString([], { dateStyle: "short", timeStyle: "short" })
        : "Never";

  // Get tag names for this device
  const deviceTags = device.tags
    .map((tagId) => groups.find((g) => g.id === tagId))
    .filter((g): g is PermissionGroup => g !== undefined);

  return (
    <div className="flex items-center gap-4 py-3 px-4 hover:bg-zinc-800/30 transition-colors border-b border-zinc-800/50 last:border-b-0 group">
      <div className="p-1.5 bg-blue-500/20 rounded">
        <Tv size={16} className="text-blue-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-white font-medium truncate">{device.label || device.id}</span>
          <StatusBadge isOnline={device.isOnline} />
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-blue-500/15 text-blue-300 rounded"
              title={`Playback time: ${formatPlaybackTime(playbackSeconds24h)} in 24h, ${formatPlaybackTime(playbackSeconds7d)} in 7d`}
            >
              <Play size={8} className="fill-current" />
              {formatPlaybackTime(playbackSeconds24h)} / 24h
            </span>
            <div
              className="flex items-center rounded bg-zinc-800/80 px-1.5 py-1"
              title={`Past 7 days: ${formatPlaybackTime(playbackSeconds7d)}`}
            >
              <PlaybackBars values={dailyPlaybackSeconds7d} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-zinc-500">
          <span>{device.id.split("-")[0]}</span>
          <span>Orientation: {formatOrientationLabel(device.orientation)}</span>
          <span>Last seen: {lastSeenText}</span>
        </div>
      </div>
      {deviceTags.length > 0 && (
        <div className="flex items-center gap-1">
          {deviceTags.map((tag) => (
            <span key={tag.id} className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded">
              {tag.name}
            </span>
          ))}
        </div>
      )}
      {/* Action buttons - visible on hover */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onViewHistory(device)}
          className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded transition-colors"
          title="View History"
        >
          <History size={16} />
        </button>
        <button
          onClick={() => onEdit(device)}
          className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded transition-colors"
          title="Edit Device"
        >
          <Edit2 size={16} />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// GroupTreeNode (Left Panel)
// ============================================================================

type GroupTreeNodeProps = {
  group: GroupWithDeviceCounts;
  level?: number;
  selectedGroupId: string | null;
  onSelect: (group: PermissionGroup) => void;
  getChildGroups: (parentId: string) => GroupWithDeviceCounts[];
  onViewHistory: (group: PermissionGroup) => void;
};

const GroupTreeNode = ({
  group,
  level = 0,
  selectedGroupId,
  onSelect,
  getChildGroups,
  onViewHistory,
}: GroupTreeNodeProps) => {
  const [expanded, setExpanded] = useState(true);

  const children = getChildGroups(group.id);
  const hasChildren = children.length > 0;
  const config = LABEL_CONFIG[group.label];
  const Icon = config.icon;
  const isSelected = selectedGroupId === group.id;

  // Calculate indentation based on level
  const indent = level * 32; // 32px per level

  return (
    <div className="relative">
      {/* Vertical connecting line from parent */}
      {level > 0 && <div className="absolute top-0 bottom-0 w-px bg-zinc-700" style={{ left: indent - 16 }} />}

      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(group)}
        onKeyDown={(e) => e.key === "Enter" && onSelect(group)}
        className={`group relative flex items-center justify-between py-3 px-4 border-b border-zinc-800/50 hover:bg-zinc-800/20 transition-colors cursor-pointer ${
          isSelected ? "bg-zinc-800/40" : ""
        }`}
        style={{ paddingLeft: indent + 16 }}
      >
        {/* Horizontal connecting line */}
        {level > 0 && <div className="absolute top-1/2 h-px bg-zinc-700" style={{ left: indent - 16, width: 12 }} />}

        {/* Left side: Icon + Name + Device counts */}
        <div className="flex items-center gap-3">
          <div className={`w-6 h-6 flex items-center justify-center rounded ${config.bgColor}`}>
            <Icon size={14} className={config.color} />
          </div>
          <span className="text-sm font-semibold text-white">{group.name}</span>

          {/* Device counts */}
          <div className="flex items-center gap-2 text-xs">
            {group.onlineCount > 0 && (
              <span className="flex items-center gap-1 text-green-400">
                <Wifi size={11} />
                {group.onlineCount}
              </span>
            )}
            {group.offlineCount > 0 && (
              <span className="flex items-center gap-1 text-zinc-500">
                <WifiOff size={11} />
                {group.offlineCount}
              </span>
            )}
          </div>

          {/* Expand/collapse chevron */}
          {hasChildren && (
            <button
              className="text-zinc-500 hover:text-white transition-colors p-0.5"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}
        </div>

        {/* History button - visible on hover */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onViewHistory(group);
          }}
          className="opacity-0 group-hover:opacity-100 p-1 text-zinc-500 hover:text-white hover:bg-zinc-700 rounded transition-all"
          title="View Playback History"
        >
          <History size={14} />
        </button>
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div className="relative">
          {children.map((child) => (
            <GroupTreeNode
              key={child.id}
              group={child}
              level={level + 1}
              selectedGroupId={selectedGroupId}
              onSelect={onSelect}
              getChildGroups={getChildGroups}
              onViewHistory={onViewHistory}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// DevicesPanel (Right Panel)
// ============================================================================

type DevicesPanelProps = {
  group: PermissionGroup;
  devices: Device[];
  allGroups: PermissionGroup[];
  playbackMap: Map<string, DevicePlaybackSummary>;
  search: string;
  onSearchChange: (value: string) => void;
  onRegister: () => void;
  onEditDevice: (device: Device) => void;
  onViewHistory: (device: Device) => void;
};

const DevicesPanel = ({
  group,
  devices,
  allGroups,
  playbackMap,
  search,
  onSearchChange,
  onRegister,
  onEditDevice,
  onViewHistory,
}: DevicesPanelProps) => {
  const config = LABEL_CONFIG[group.label];
  const Icon = config.icon;

  const onlineCount = devices.filter((d) => d.isOnline).length;
  const offlineCount = devices.length - onlineCount;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-zinc-800">
        <span
          className={`flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide rounded ${config.bgColor} ${config.color}`}
        >
          <Icon size={12} />
          {LABEL_CONFIG[group.label].displayName}
        </span>
        <h2 className="text-lg font-semibold text-white flex-1">{group.name}</h2>
        <button
          onClick={onRegister}
          className="flex items-center gap-1.5 text-xs font-semibold text-zinc-400 hover:text-white transition-all uppercase tracking-wide"
        >
          <Plus size={14} />
          Register Device
        </button>
      </div>

      {/* Search and Stats */}
      <div className="p-4 border-b border-zinc-800 space-y-3">
        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search devices..."
            className="w-full pl-9 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600"
          />
        </div>

        {/* Stats */}
        <div className="flex gap-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-zinc-400">Total:</span>
            <span className="text-white font-medium">{devices.length}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Wifi size={14} className="text-green-400" />
            <span className="text-green-400">{onlineCount}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <WifiOff size={14} className="text-zinc-500" />
            <span className="text-zinc-500">{offlineCount}</span>
          </div>
        </div>
      </div>

      {/* Devices list */}
      <div className="flex-1 overflow-auto p-4">
        {devices.length === 0 ? (
          <div className="text-zinc-500 py-8 text-center">
            <Tv size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">{search ? "No devices match your search" : "No devices in this group"}</p>
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
            {devices.map((device) => (
              <DeviceRow
                key={device.id}
                device={device}
                groups={allGroups}
                playbackSeconds24h={playbackMap.get(device.id)?.playbackSeconds24h}
                playbackSeconds7d={playbackMap.get(device.id)?.playbackSeconds7d}
                dailyPlaybackSeconds7d={playbackMap.get(device.id)?.dailyPlaybackSeconds7d}
                lastPlaybackAt={playbackMap.get(device.id)?.lastPlaybackAt}
                onEdit={onEditDevice}
                onViewHistory={onViewHistory}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// RegisterDeviceDialog
// ============================================================================

type RegisterDeviceDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  groups: PermissionGroup[];
  onSuccess: () => void;
  defaultSiteId?: string | null;
  deviceCount?: number;
  deviceLimit?: number;
};

const RegisterDeviceDialog = ({
  isOpen,
  onClose,
  groups,
  onSuccess,
  defaultSiteId,
  deviceCount,
  deviceLimit,
}: RegisterDeviceDialogProps) => {
  const apiClient = useApiClient();
  const { currentTenantId } = useTenant();
  const [pairingCode, setPairingCode] = useState("");
  const [label, setLabel] = useState("");
  const [selectedSiteId, setSelectedSiteId] = useState(defaultSiteId || "");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [tagsDropdownOpen, setTagsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset selectedSiteId when defaultSiteId changes (dialog opens with new selection)
  useEffect(() => {
    if (isOpen) {
      setSelectedSiteId(defaultSiteId || "");
    }
  }, [isOpen, defaultSiteId]);

  const tags = groups.filter((g) => g.label === "tag");

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]));
  };

  const resetForm = () => {
    setPairingCode("");
    setLabel("");
    setSelectedSiteId("");
    setSelectedTagIds([]);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await apiClient.post("/api/devices/register", {
        pairingCode: pairingCode.toUpperCase(),
        siteId: selectedSiteId || undefined,
        label: label || undefined,
        tags: selectedTagIds.length > 0 ? selectedTagIds : undefined,
        tenantId: currentTenantId ?? undefined,
      });

      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to register device");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />

      <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h2 className="text-lg font-semibold text-white">Register Device</h2>
          <button onClick={handleClose} className="p-1 text-zinc-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">{error}</div>
          )}

          {/* Pairing Code */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">Pairing Code</label>
            <input
              type="text"
              value={pairingCode}
              onChange={(e) => setPairingCode(e.target.value.toUpperCase())}
              placeholder="Enter 5-character code"
              maxLength={5}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500 font-mono text-lg tracking-widest text-center"
              required
            />
            <p className="text-xs text-zinc-500 mt-1">Enter the code shown on the TV screen</p>
          </div>

          {/* Device Label */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">Device Name (optional)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Lobby TV, Bar Display"
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Site Selection */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">Site (optional)</label>
            <HierarchyPicker
              groups={groups}
              selectedId={selectedSiteId || null}
              onChange={(groupId) => setSelectedSiteId(groupId || "")}
              placeholder="Select a site..."
            />
          </div>

          {/* Tags Selection */}
          {tags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Tags (optional)</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setTagsDropdownOpen(!tagsDropdownOpen);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-left focus:outline-none focus:border-blue-500"
                >
                  <span className={selectedTagIds.length > 0 ? "text-white" : "text-zinc-500"}>
                    {selectedTagIds.length > 0
                      ? `${selectedTagIds.length} tag${selectedTagIds.length > 1 ? "s" : ""} selected`
                      : "Select tags"}
                  </span>
                  <ChevronDown size={16} className="text-zinc-400" />
                </button>

                {tagsDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg max-h-48 overflow-auto">
                    {tags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-700 transition-colors flex items-center gap-2 ${
                          selectedTagIds.includes(tag.id) ? "bg-zinc-700 text-white" : "text-zinc-300"
                        }`}
                      >
                        <span
                          className={`w-4 h-4 rounded border ${
                            selectedTagIds.includes(tag.id) ? "bg-purple-500 border-purple-500" : "border-zinc-600"
                          } flex items-center justify-center`}
                        >
                          {selectedTagIds.includes(tag.id) && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </span>
                        {tag.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedTagIds.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {selectedTagIds.map((tagId) => {
                    const tag = tags.find((t) => t.id === tagId);
                    return tag ? (
                      <span
                        key={tagId}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded"
                      >
                        {tag.name}
                        <button type="button" onClick={() => toggleTag(tagId)} className="hover:text-purple-300">
                          <X size={12} />
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between gap-3 pt-2">
            <span className="text-xs text-zinc-500">
              {deviceCount !== undefined && deviceLimit !== undefined ? `Total: ${deviceCount} / ${deviceLimit}` : ""}
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!pairingCode || pairingCode.length !== 5 || isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg transition-colors"
              >
                {isSubmitting ? "Registering..." : "Register Device"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// Edit Device Dialog
// ============================================================================

type EditDeviceDialogProps = {
  device: Device | null;
  onClose: () => void;
  groups: PermissionGroup[];
  onSuccess: () => void;
};

const EditDeviceDialog = ({ device, onClose, groups, onSuccess }: EditDeviceDialogProps) => {
  const apiClient = useApiClient();
  const [label, setLabel] = useState(device?.label || "");
  const [selectedSiteId, setSelectedSiteId] = useState(device?.site_id || "");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(device?.tags || []);
  const [status, setStatus] = useState<"registered" | "inactive">(
    device?.status === "inactive" ? "inactive" : "registered",
  );
  const [orientation, setOrientation] = useState<Device["orientation"]>(device?.orientation ?? "landscape");
  const [tagsDropdownOpen, setTagsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when device changes
  useEffect(() => {
    if (device) {
      setLabel(device.label || "");
      setSelectedSiteId(device.site_id || "");
      setSelectedTagIds(device.tags || []);
      setStatus(device.status === "inactive" ? "inactive" : "registered");
      setOrientation(device.orientation ?? "landscape");
      setError(null);
    }
  }, [device]);

  const tags = groups.filter((g) => g.label === "tag");

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!device) return;

    setError(null);
    setIsSubmitting(true);

    try {
      await apiClient.put(`/api/devices/${device.id}`, {
        siteId: selectedSiteId || null,
        label: label || null,
        tags: selectedTagIds,
        status,
        orientation,
      });

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update device");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!device) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h2 className="text-lg font-semibold text-white">Edit Device</h2>
          <button onClick={onClose} className="p-1 text-zinc-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">{error}</div>
          )}

          {/* Device ID (read-only) */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">Device ID</label>
            <input
              type="text"
              value={device.id}
              disabled
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-500 text-sm"
            />
          </div>

          {/* Device Label */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">Device Name</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Lobby TV, Bar Display"
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Site Selection */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">Site</label>
            <HierarchyPicker
              groups={groups}
              selectedId={selectedSiteId || null}
              onChange={(groupId) => setSelectedSiteId(groupId || "")}
              placeholder="Select a site..."
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as "registered" | "inactive")}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
            >
              <option value="registered">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">Orientation</label>
            <select
              value={orientation}
              onChange={(e) => setOrientation(e.target.value as Device["orientation"])}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
            >
              <option value="landscape">Landscape</option>
              <option value="portrait-left">Portrait Left</option>
              <option value="portrait-right">Portrait Right</option>
              <option value="landscape-inverted">Landscape Inverted</option>
            </select>
          </div>

          {/* Tags Selection */}
          {tags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Tags</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setTagsDropdownOpen(!tagsDropdownOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-left focus:outline-none focus:border-blue-500"
                >
                  <span className={selectedTagIds.length > 0 ? "text-white" : "text-zinc-500"}>
                    {selectedTagIds.length > 0
                      ? `${selectedTagIds.length} tag${selectedTagIds.length > 1 ? "s" : ""} selected`
                      : "Select tags"}
                  </span>
                  <ChevronDown size={16} className="text-zinc-400" />
                </button>

                {tagsDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg max-h-48 overflow-auto">
                    {tags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-700 transition-colors flex items-center gap-2 ${
                          selectedTagIds.includes(tag.id) ? "bg-zinc-700 text-white" : "text-zinc-300"
                        }`}
                      >
                        <span
                          className={`w-4 h-4 rounded border ${
                            selectedTagIds.includes(tag.id) ? "bg-purple-500 border-purple-500" : "border-zinc-600"
                          } flex items-center justify-center`}
                        >
                          {selectedTagIds.includes(tag.id) && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </span>
                        {tag.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedTagIds.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {selectedTagIds.map((tagId) => {
                    const tag = tags.find((t) => t.id === tagId);
                    return tag ? (
                      <span
                        key={tagId}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded"
                      >
                        {tag.name}
                        <button type="button" onClick={() => toggleTag(tagId)} className="hover:text-purple-300">
                          <X size={12} />
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg transition-colors"
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// Device History Panel
// ============================================================================

type HistoryTarget =
  | { type: "device"; device: Device }
  | { type: "group"; group: PermissionGroup; deviceIds: string[] };

type DeviceHistoryPanelProps = {
  target: HistoryTarget | null;
  onClose: () => void;
};

type EventFilter = "all" | "playback" | "errors";

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

const formatDateHeading = (dateStr: string): string => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
};

const getDefaultDateRange = (): { from: string; to: string } => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 7);
  return {
    from: from.toISOString().split("T")[0] as string,
    to: to.toISOString().split("T")[0] as string,
  };
};

const groupEventsByDate = (events: DeviceEvent[]): Map<string, DeviceEvent[]> => {
  const groups = new Map<string, DeviceEvent[]>();
  for (const event of events) {
    const dateKey = new Date(event.createdAt).toDateString();
    const existing = groups.get(dateKey);
    if (existing) {
      existing.push(event);
    } else {
      groups.set(dateKey, [event]);
    }
  }
  return groups;
};

const EventRow = ({ event, showDevice }: { event: DeviceEvent; showDevice?: boolean }) => {
  const [showDetail, setShowDetail] = useState(false);
  const data = event.data;
  const mediaName = (data.mediaName as string) || null;
  const mediaType = (data.mediaType as string) || null;
  const slideshowName = (data.slideshowName as string) || null;
  const scheduleName = (data.scheduleName as string) || null;
  const duration = (data.duration as number) || null;
  const userMessage = (data.userMessage as string) || (data.error as string) || null;
  const errorDetail = (data.errorDetail as string) || null;
  const referenceId = (data.referenceId as string) || null;
  const deviceLabel = (data.deviceLabel as string) || event.actorId || null;
  const isError = event.eventType.includes("error");
  const isStarted = event.eventType.includes("started");
  const isVideo = mediaType?.startsWith("video");

  const handleErrorClick = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      setShowDetail((prev) => !prev);
    }
  };

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/40 border border-zinc-800/60 hover:border-zinc-700 transition-colors">
      {/* Type icon */}
      <div className="w-12 h-12 rounded-lg bg-zinc-800 border border-zinc-700/50 flex-shrink-0 overflow-hidden flex items-center justify-center">
        {isError ? (
          <AlertCircle size={18} className="text-red-400" />
        ) : isVideo ? (
          <Film size={18} className="text-zinc-500" />
        ) : (
          <Image size={18} className="text-zinc-500" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${isError ? "text-red-400" : "text-white"}`}>
            {mediaName || event.resourceId || "Unknown media"}
          </span>
          {isError && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-500/20 text-red-400 rounded">ERROR</span>
          )}
          {isStarted && <Play size={12} className="text-emerald-400 flex-shrink-0" />}
        </div>

        {/* Context tags */}
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {showDevice && deviceLabel && (
            <span className="text-[11px] text-cyan-400/80 bg-cyan-500/10 px-1.5 py-0.5 rounded">
              <Tv size={10} className="inline mr-0.5 -mt-px" />
              {deviceLabel}
            </span>
          )}
          {slideshowName && (
            <span className="text-[11px] text-zinc-400 bg-zinc-800 px-1.5 py-0.5 rounded">{slideshowName}</span>
          )}
          {scheduleName && (
            <span className="text-[11px] text-blue-400/80 bg-blue-500/10 px-1.5 py-0.5 rounded">
              <Calendar size={10} className="inline mr-0.5 -mt-px" />
              {scheduleName}
            </span>
          )}
          {duration != null && (
            <span className="text-[11px] text-zinc-500">
              <Clock size={10} className="inline mr-0.5 -mt-px" />
              {formatDuration(duration)}
            </span>
          )}
        </div>

        {/* Error info: generic message + reference ID, cmd/ctrl+click to expand detail */}
        {isError && userMessage && (
          <p
            className="text-xs text-red-400/80 mt-1 cursor-default select-text"
            onClick={handleErrorClick}
            title={errorDetail ? "Cmd+click to show/hide error details" : undefined}
          >
            {userMessage}
            {referenceId && <span className="ml-2 text-zinc-500 font-mono text-[10px]">{referenceId}</span>}
          </p>
        )}
        {showDetail && errorDetail && (
          <pre className="text-[11px] text-red-300/60 mt-1 whitespace-pre-wrap break-all bg-red-950/30 border border-red-900/30 rounded p-2 font-mono">
            {errorDetail}
          </pre>
        )}
      </div>

      {/* Timestamp */}
      <span className="text-[11px] text-zinc-500 flex-shrink-0 mt-0.5">
        {new Date(event.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </span>
    </div>
  );
};

const PAGE_SIZE = 100;

const DeviceHistoryPanel = ({ target, onClose }: DeviceHistoryPanelProps) => {
  const apiClient = useApiClient();
  const [events, setEvents] = useState<DeviceEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState<EventFilter>("all");
  const [dateEnabled, setDateEnabled] = useState(false);
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [useLocalTime, setUseLocalTime] = useState(true);
  const [exporting, setExporting] = useState(false);

  const isGroupMode = target?.type === "group";

  const buildDateParams = () => {
    if (!dateEnabled) return {};
    if (useLocalTime) {
      const fromDate = new Date(`${dateRange.from}T00:00:00`);
      const toDate = new Date(`${dateRange.to}T00:00:00`);
      toDate.setDate(toDate.getDate() + 1);
      return { from: fromDate.toISOString(), to: toDate.toISOString(), precise: true };
    }
    return { from: dateRange.from, to: dateRange.to, precise: false };
  };

  const getEventType = () => {
    if (filter === "playback") return "media.playback.ended";
    if (filter === "errors") return "media.playback.error";
    return undefined;
  };

  useEffect(() => {
    if (!target) return;

    const fetchEvents = async () => {
      setLoading(true);
      try {
        if (target.type === "device") {
          // Single device - use existing endpoint
          const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: "0" });
          const dateParams = buildDateParams();
          if (dateParams.from) params.set("from", dateParams.from);
          if (dateParams.to) params.set("to", dateParams.to);
          if (dateParams.precise) params.set("precise", "true");
          const eventType = getEventType();
          if (eventType) params.set("type", eventType);

          const data = await apiClient.get<{ events: DeviceEvent[] }>(
            `/api/devices/${target.device.id}/events?${params.toString()}`,
          );
          setEvents(data.events);
          setHasMore(data.events.length >= PAGE_SIZE);
        } else {
          // Group mode - use multi-device endpoint
          const data = await apiClient.post<{ events: DeviceEvent[] }>("/api/devices/events/query", {
            deviceIds: target.deviceIds,
            limit: PAGE_SIZE,
            offset: 0,
            type: getEventType(),
            ...buildDateParams(),
          });
          setEvents(data.events);
          setHasMore(data.events.length >= PAGE_SIZE);
        }
      } catch (err) {
        console.error("Failed to fetch events:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [target, apiClient, filter, dateEnabled, dateRange, useLocalTime]);

  const loadMore = async () => {
    if (!target || loadingMore) return;
    setLoadingMore(true);
    try {
      if (target.type === "device") {
        const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(events.length) });
        const dateParams = buildDateParams();
        if (dateParams.from) params.set("from", dateParams.from);
        if (dateParams.to) params.set("to", dateParams.to);
        if (dateParams.precise) params.set("precise", "true");
        const eventType = getEventType();
        if (eventType) params.set("type", eventType);

        const data = await apiClient.get<{ events: DeviceEvent[] }>(
          `/api/devices/${target.device.id}/events?${params.toString()}`,
        );
        setEvents((prev) => [...prev, ...data.events]);
        setHasMore(data.events.length >= PAGE_SIZE);
      } else {
        const data = await apiClient.post<{ events: DeviceEvent[] }>("/api/devices/events/query", {
          deviceIds: target.deviceIds,
          limit: PAGE_SIZE,
          offset: events.length,
          type: getEventType(),
          ...buildDateParams(),
        });
        setEvents((prev) => [...prev, ...data.events]);
        setHasMore(data.events.length >= PAGE_SIZE);
      }
    } catch (err) {
      console.error("Failed to load more events:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleExportWorkbook = async () => {
    if (!target) return;

    // CSV export requires a date range
    const dateParams = dateEnabled
      ? buildDateParams()
      : (() => {
          // Default to last 30 days if no date range set
          const from = new Date();
          from.setDate(from.getDate() - 30);
          const to = new Date();
          to.setDate(to.getDate() + 1);
          return { from: from.toISOString(), to: to.toISOString(), precise: true };
        })();

    const deviceIds = target.type === "device" ? [target.device.id] : target.deviceIds;

    setExporting(true);
    try {
      const response = await fetch("/api/devices/events/export", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify({
          deviceIds,
          type: getEventType(),
          ...dateParams,
        }),
      });

      if (!response.ok) throw new Error("Export failed");

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `playback-history-${new Date().toISOString().split("T")[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export workbook:", err);
    } finally {
      setExporting(false);
    }
  };

  if (!target) return null;

  const grouped = groupEventsByDate(events);
  const title = target.type === "device" ? "Device History" : "Group History";
  const subtitle =
    target.type === "device"
      ? target.device.label || target.device.id
      : `${target.group.name} (${target.deviceIds.length} devices)`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl w-full max-w-4xl mx-4 min-h-[60vh] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <History size={18} className="text-zinc-400" />
              {title}
            </h2>
            <p className="text-sm text-zinc-400 mt-0.5">{subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-zinc-800">
          {/* Event type filter */}
          <div className="flex items-center gap-1">
            <Filter size={14} className="text-zinc-500 mr-1" />
            {(["all", "playback", "errors"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  filter === f ? "bg-zinc-700 text-white" : "text-zinc-400 hover:text-zinc-300 hover:bg-zinc-800"
                }`}
              >
                {f === "playback" ? "Playback" : f === "errors" ? "Errors" : "All Events"}
              </button>
            ))}
          </div>

          {/* Date range toggle + inputs + Export */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDateEnabled((prev) => !prev)}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                dateEnabled
                  ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                  : "text-zinc-400 hover:text-zinc-300 hover:bg-zinc-800"
              }`}
            >
              <Calendar size={12} />
              Date Range
            </button>
            {dateEnabled && (
              <>
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-md px-2 py-1.5 text-xs text-zinc-300 focus:outline-none focus:border-zinc-500"
                />
                <span className="text-xs text-zinc-500">to</span>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-md px-2 py-1.5 text-xs text-zinc-300 focus:outline-none focus:border-zinc-500"
                />
                <button
                  onClick={() => setUseLocalTime((prev) => !prev)}
                  title={useLocalTime ? "Using local time" : "Using UTC"}
                  className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                    useLocalTime
                      ? "bg-zinc-700 text-zinc-300"
                      : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                  }`}
                >
                  {useLocalTime ? <Clock size={12} /> : <Globe size={12} />}
                  {useLocalTime ? "Local" : "UTC"}
                </button>
              </>
            )}
            <div className="w-px h-5 bg-zinc-700" />
            <button
              onClick={handleExportWorkbook}
              disabled={exporting}
              title={dateEnabled ? "Export filtered range to Excel" : "Export last 30 days to Excel"}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md text-zinc-400 hover:text-zinc-300 hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              <Download size={12} />
              {exporting ? "Exporting..." : "Export Excel"}
            </button>
          </div>
        </div>

        {/* Event list */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="text-center py-16 text-zinc-500">Loading...</div>
          ) : events.length === 0 ? (
            <div className="text-center py-16 text-zinc-500">
              <History size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">
                {isGroupMode ? "No events recorded for devices in this group" : "No events recorded for this device"}
              </p>
            </div>
          ) : (
            <div className="p-5 space-y-5">
              {Array.from(grouped.entries()).map(([dateKey, dayEvents]) => (
                <div key={dateKey}>
                  <div className="flex items-center justify-between mb-2.5 px-1">
                    <span className="text-sm font-medium text-zinc-300">{formatDateHeading(dateKey)}</span>
                    <span className="text-xs text-zinc-500">
                      {dayEvents.length} {dayEvents.length === 1 ? "event" : "events"}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {dayEvents.map((event) => (
                      <EventRow key={event.id} event={event} showDevice={isGroupMode} />
                    ))}
                  </div>
                </div>
              ))}

              {/* Load more */}
              {hasMore && (
                <div className="text-center pt-2 pb-1">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-4 py-2 text-sm font-medium text-zinc-300 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loadingMore ? "Loading..." : "Load more"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer with count */}
        {events.length > 0 && (
          <div className="px-5 py-2.5 border-t border-zinc-800 text-xs text-zinc-500">
            Showing {events.length} events{hasMore ? " (more available)" : ""}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main Screen
// ============================================================================

const DevicesContent = () => {
  const apiClient = useApiClient();
  const { currentTenantId } = useTenant();
  const [devices, setDevices] = useState<Device[]>([]);
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [historyTarget, setHistoryTarget] = useState<HistoryTarget | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<PermissionGroup | null>(null);
  const [playbackMap, setPlaybackMap] = useState<Map<string, DevicePlaybackSummary>>(new Map());
  const [limitMessage, setLimitMessage] = useState<string | null>(null);
  const [deviceCapacity, setDeviceCapacity] = useState<{ count: number; limit: number } | null>(null);

  const handleRegisterClick = async () => {
    if (!currentTenantId) return;
    try {
      const result = await apiClient.get<{ canAdd: boolean; currentCount: number; limit: number }>(
        `/api/devices/can-add?tenantId=${currentTenantId}`,
      );
      if (!result.canAdd) {
        setLimitMessage(
          `Device limit reached (${result.currentCount} of ${result.limit}). Please delete a device before adding more.`,
        );
        return;
      }
      setDeviceCapacity({ count: result.currentCount, limit: result.limit });
      setDialogOpen(true);
    } catch (err) {
      setLimitMessage(err instanceof Error ? err.message : "Failed to check device limit");
    }
  };

  const fetchDevices = async () => {
    if (!currentTenantId) {
      setDevices([]);
      return null;
    }
    try {
      const params = new URLSearchParams({ tenantId: currentTenantId });
      const data = await apiClient.get<{ devices: Device[] }>(`/api/devices?${params}`);
      setDevices(data.devices);
      return data.devices;
    } catch (err) {
      console.error("Failed to fetch devices:", err);
      return null;
    }
  };

  const fetchGroups = async () => {
    if (!currentTenantId) {
      setGroups([]);
      return;
    }
    try {
      const params = new URLSearchParams({ tenantId: currentTenantId });
      const data = await apiClient.get<{ groups: PermissionGroup[] }>(`/api/groups?${params}`);
      setGroups(data.groups);
    } catch (err) {
      console.error("Failed to fetch groups:", err);
    }
  };

  const fetchPlayback = async (deviceIds: string[]) => {
    if (deviceIds.length === 0) return;
    try {
      const data = await apiClient.post<{ playback: DevicePlaybackSummary[] }>("/api/devices/uptime", { deviceIds });
      const map = new Map<string, DevicePlaybackSummary>();
      for (const item of data.playback) map.set(item.deviceId, item);
      setPlaybackMap(map);
    } catch (err) {
      console.error("Failed to fetch playback totals:", err);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [fetchedDevices] = await Promise.all([fetchDevices(), fetchGroups()]);
      setLoading(false);
      // Fetch playback totals after initial load (non-blocking)
      if (fetchedDevices) {
        fetchPlayback(fetchedDevices.map((d) => d.id));
      }
    };
    load();

    const interval = setInterval(fetchDevices, 10_000);
    return () => clearInterval(interval);
  }, [apiClient]);

  // Get devices for a specific group (by site_id or tags)
  const getDevicesForGroup = (groupId: string): Device[] => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return [];

    if (group.label === "tag") {
      // Check both site_id and tags array — devices can be assigned via either
      return devices.filter((d) => d.site_id === groupId || d.tags.includes(groupId));
    }
    return devices.filter((d) => d.site_id === groupId);
  };

  // Get all devices under a group (including from child groups)
  const getAllDevicesUnderGroup = (groupId: string): Device[] => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return [];

    // Direct devices for this group
    const directDevices = getDevicesForGroup(groupId);

    // Devices from child groups
    const children = getChildGroupsUtil(groups, groupId);
    const childDevices = children.flatMap((child) => getAllDevicesUnderGroup(child.id));

    // Combine and deduplicate
    const allDevices = [...directDevices, ...childDevices];
    const uniqueDevices = Array.from(new Map(allDevices.map((d) => [d.id, d])).values());
    return uniqueDevices;
  };

  // Get child groups with device counts
  const getChildGroupsWithCounts = (parentId: string): GroupWithDeviceCounts[] => {
    return getChildGroupsUtil(groups, parentId).map((group) => {
      const groupDevices = getAllDevicesUnderGroup(group.id);
      return {
        ...group,
        onlineCount: groupDevices.filter((d) => d.isOnline).length,
        offlineCount: groupDevices.filter((d) => !d.isOnline).length,
      };
    });
  };

  // Get root groups with device counts
  const getRootGroupsWithCounts = (): GroupWithDeviceCounts[] => {
    return getRootGroupsUtil(groups).map((group) => {
      const groupDevices = getAllDevicesUnderGroup(group.id);
      return {
        ...group,
        onlineCount: groupDevices.filter((d) => d.isOnline).length,
        offlineCount: groupDevices.filter((d) => !d.isOnline).length,
      };
    });
  };

  // Filter devices for selected group by search
  const getFilteredDevicesForSelectedGroup = (): Device[] => {
    if (!selectedGroup) return [];

    const groupDevices = getAllDevicesUnderGroup(selectedGroup.id);
    if (!search) return groupDevices;

    const searchLower = search.toLowerCase();
    return groupDevices.filter((device) => {
      const site = groups.find((g) => g.id === device.site_id);
      const deviceTags = device.tags.map((tagId: string) => groups.find((g) => g.id === tagId)?.name || "").join(" ");
      return (
        (device.label || "").toLowerCase().includes(searchLower) ||
        device.id.toLowerCase().includes(searchLower) ||
        site?.name.toLowerCase().includes(searchLower) ||
        deviceTags.toLowerCase().includes(searchLower)
      );
    });
  };

  const rootGroups = getRootGroupsWithCounts();
  const filteredDevices = getFilteredDevicesForSelectedGroup();

  if (loading) {
    return (
      <div className="flex-1 p-6 flex items-center justify-center">
        <div className="text-zinc-500">Loading devices...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-6 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Devices</h1>
          <p className="text-sm text-zinc-400 mt-1">Manage your Android TV devices</p>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden px-6 pb-6 gap-4">
        {/* Left: Groups tree */}
        <div className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg overflow-auto">
          <div className="p-2">
            {groups.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                <Tv size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No groups found</p>
              </div>
            ) : (
              rootGroups.map((group) => (
                <GroupTreeNode
                  key={group.id}
                  group={group}
                  selectedGroupId={selectedGroup?.id ?? null}
                  onSelect={setSelectedGroup}
                  getChildGroups={getChildGroupsWithCounts}
                  onViewHistory={(g) => {
                    const deviceIds = getAllDevicesUnderGroup(g.id).map((d) => d.id);
                    setHistoryTarget({ type: "group", group: g, deviceIds });
                  }}
                />
              ))
            )}
          </div>
        </div>

        {/* Right: Selected group's devices */}
        <div className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          {selectedGroup ? (
            <DevicesPanel
              group={selectedGroup}
              devices={filteredDevices}
              allGroups={groups}
              playbackMap={playbackMap}
              search={search}
              onSearchChange={setSearch}
              onRegister={handleRegisterClick}
              onEditDevice={setEditingDevice}
              onViewHistory={(device) => setHistoryTarget({ type: "device", device })}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center h-full text-zinc-500">
              <div className="text-center">
                <Tv size={48} className="mx-auto mb-4 opacity-50" />
                <p>Select a group to view devices</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Register Dialog */}
      <RegisterDeviceDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        groups={groups}
        onSuccess={fetchDevices}
        defaultSiteId={selectedGroup?.id}
        deviceCount={deviceCapacity?.count}
        deviceLimit={deviceCapacity?.limit}
      />

      {/* Edit Device Dialog */}
      <EditDeviceDialog
        device={editingDevice}
        onClose={() => setEditingDevice(null)}
        groups={groups}
        onSuccess={fetchDevices}
      />

      {/* Device History Panel */}
      <DeviceHistoryPanel target={historyTarget} onClose={() => setHistoryTarget(null)} />

      {/* Device limit reached */}
      {limitMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setLimitMessage(null)} />
          <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl w-full max-w-md mx-4 p-5">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-white">Cannot register device</h2>
                <p className="text-sm text-zinc-300 mt-2">{limitMessage}</p>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setLimitMessage(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default () => {
  const { currentTenantId } = useTenant();
  // Remount on tenant switch so all local state (devices, groups, selection) resets cleanly
  return <DevicesContent key={currentTenantId ?? "no-tenant"} />;
};
